<template>
  <div class="container">
    <div class="jumbotron">
      <h1>Payment</h1>
    </div>

    <payment-form
      :attestations="attestations"
      :global-error="error"
      :loading="loading"
      :loading-estimates="estimatesLoading"
      :states="states"
      :stripe-elements="stripeElements"
      :user="user"
      @add-territories="onAddTerritories"
      @change="onFormChange"
      @pay="onPay"
    >
      <payment-estimates
        :error="estimatesError"
        :estimates="invoiceEstimates"
        :loading="estimatesLoading"
        :products="products"
        @item-change="onItemChange"
        :estimation-complete="estimationComplete"
        class="mt-3"
      />
    </payment-form>

    <p>By continuing, you understand that you are responsible for any applicable SaaS sales tax that is charged by <a
      target="_blank" href="https://www.taxjar.com/sales-tax/saas-sales-tax">your state's tax law.</a></p>
  </div>
</template>

<script>
import swal from 'sweetalert2';
import PaymentEstimates from '../components/payment/PaymentEstimates.vue';
import PaymentForm from '../components/payment/PaymentForm';
import {checkout} from '../utility/api/billing/checkout';
import {createPaymentIntent} from '../utility/api/billing/createPaymentIntent';
import {estimate} from '../utility/api/billing/estimate';
import {formatErrorResponse} from '../utility/api/formatErrorResponse';
import {getUserProfile} from '../utility/api/user/getUserProfile';
import {getStates} from '../utility/api/utility/getStates';
import {getVessel} from '../utility/api/vessel/getVessel';
import {getQueryParamsMap} from '../utility/router/getQueryParamsMap';
import {loadStripeScript} from '../utility/stripe/loadStripeScript';

export default {
  name: 'payment-page',
  components: {PaymentEstimates, PaymentForm},
  data() {
    const paramsMap = getQueryParamsMap();
    const cartProducts = paramsMap['c']
      ? paramsMap['c'].split(',').map(product => {
          const [name, quantity] = product.split(':');
          return {name, quantity: quantity && Number(quantity)};
        })
      : [];

    return {
      attestations: [],
      cartProducts,
      error: '',
      estimates: [],
      estimatesError: '',
      estimatesLoading: false,
      formData: {},
      loading: false,
      products: [],
      states: [],
      stripe: null,
      stripeElements: null,
      userProfile: {},
      vessel: null,
      estimateId: null,
      estimationComplete: false,
    };
  },
  props: {
    stripeKey: {
      type: String,
      required: true,
    },
  },
  computed: {
    invoiceEstimates() {
      return this.estimates.filter(estimate => estimate.invoice_estimate);
    },
    invoiceTotal() {
      return this.invoiceEstimates.reduce((total, estimate) => total + estimate.invoice_estimate.total, 0);
    },
    user() {
      const [employee] = (this.vessel && this.vessel.employees && this.vessel.employees.data) || [];
      return employee;
    },
  },
  watch: {
    cartProducts(cartProducts) {
      const paramsMap = getQueryParamsMap();
      const c = cartProducts.map(({name, quantity}) => (quantity ? `${name}:${quantity}` : name)).join(',');

      this.updateUrl({...paramsMap, c});
      this.getEstimates(cartProducts, this.formData);
    },
    formData({coupons, address}) {
      this.getEstimates(this.cartProducts, {coupons, address});
    },
  },
  methods: {
    updateUrl(paramsMap) {
      const {pathname} = window.location;
      const query = Object.entries(paramsMap)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      const url = `${pathname}?${query}`;

      window.history.replaceState(undefined, undefined, url);
    },
    async createPaymentMethod() {
      const cardNumberElement = this.stripeElements.getElement('cardNumber');

      const {error, paymentMethod} = await this.stripe.createPaymentMethod({
        type: 'card',
        card: cardNumberElement,
      });

      if (error) {
        this.error = error.message;
        throw error;
      }

      return paymentMethod;
    },
    async handleCardAction(clientSecret) {
      const {error, paymentIntent} = await this.stripe.handleCardAction(clientSecret);

      if (error) {
        this.error = error.message;
        throw error;
      }

      return paymentIntent;
    },
    async createPaymentIntent(formData, paymentMethod) {
      const response = await createPaymentIntent({
        payment_method_id: paymentMethod.id,
        estimate_id: this.estimateId,
        products: this.cartProducts,
        coupons: formData.coupons,
        address: formData.address,
      });

      if (response.requires_action) {
        const paymentIntent = await this.handleCardAction(response.payment_intent_client_secret);
        return paymentIntent.id;
      } else if (response.success) {
        return response.payment_intent_id;
      } else {
        this.error = response.error;
        throw Error(this.error);
      }
    },
    async createCardToken() {
      const cardNumberElement = this.stripeElements.getElement('cardNumber');

      const {error, token} = await this.stripe.createToken(cardNumberElement);

      if (error) {
        this.error = error.message;
        throw error;
      }

      return token;
    },
    async checkout(cardToken, formData, paymentIntentId) {
      const {meta} = await checkout({
        card_token_id: cardToken?.id,
        payment_intent_id: paymentIntentId,
        estimate_id: this.estimateId,
        products: this.cartProducts,
        coupons: formData.coupons,
        address: formData.address,
      });

      return meta?.redirect_url;
    },
    async onPay(formData) {
      this.loading = true;

      try {
        let cardToken = undefined;
        let paymentIntentId = undefined;
        if (this.invoiceTotal > 0) {
          const paymentMethod = await this.createPaymentMethod();
          paymentIntentId = await this.createPaymentIntent(formData, paymentMethod);
        } else {
          cardToken = await this.createCardToken();
        }

        const redirectUrl = await this.checkout(cardToken, formData, paymentIntentId);
        await this.showSuccessMessage();
        window.location.assign(redirectUrl || '/library');
      } catch (error) {
        this.loading = false;

        console.error(error);

        if (error.response && error.response.data) {
          this.error = formatErrorResponse(error.response.data);
        } else {
          this.error = error.message || 'Something went wrong.';
        }
      }
    },
    redirectOnSuccess() {
      const hasConvos = this.cartProducts.some(({name}) => name.toUpperCase().startsWith('CONVOS'));
      window.location.assign(hasConvos ? '/convos-product-confirmation' : '/library');
    },
    onAddTerritories() {
      this.cartProducts = this.cartProducts.filter(({name}) => name !== 'CMAT').concat({name: 'CMAT', quantity: 1});
    },
    onFormChange({coupons, address}) {
      this.formData = {coupons, address};
    },
    onItemChange({id, quantity}) {
      const {name} = this.products.find(p => p.id === id) || {};

      if (quantity) {
        this.cartProducts = this.cartProducts.map(product =>
          product.name === name ? {...product, quantity} : product
        );
      } else {
        this.cartProducts = this.cartProducts.filter(product => product.name !== name);
      }
    },
    async initStripe() {
      await loadStripeScript();

      this.stripe = Stripe(this.stripeKey);
      this.stripeElements = this.stripe.elements({locale: 'en-US'});
    },
    showSuccessMessage() {
      return swal.fire({
        title: 'Success!',
        text: 'Your payment has been processed and you can now use the platform.',
        type: 'success',
        confirmButtonText: 'Ok',
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-lg btn-primary',
      });
    },
    showNoProductMessage() {
      return swal.fire({
        title: 'Oh no!',
        text: 'No product has been selected. You will be redirected to homepage since there is nothing to pay for.',
        type: 'error',
        confirmButtonText: 'Ok',
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-lg btn-primary',
      });
    },
    async checkCartProducts() {
      if (!this.cartProducts.length) {
        await this.showNoProductMessage();
        window.location.assign('/');
      }
    },
    async getEstimates(products, {coupons, address} = {}) {
      this.estimatesError = '';
      this.estimatesLoading = true;

      try {
        const {data, meta} = await estimate({coupons, products, address});

        this.estimationComplete = true;

        this.estimates = data;

        this.attestations = meta.attestations || [];
        this.products = meta.products || [];
        this.estimateId = meta.estimate_id;
      } catch (error) {
        console.error(error);

        if (error.response && error.response.data) {
          this.estimatesError = formatErrorResponse(error.response.data);
        } else {
          this.estimatesError = 'Something went wrong.';
        }

        this.estimates = [];
      }

      this.estimatesLoading = false;
    },
  },
  async created() {
    this.states = await getStates();
    this.userProfile = await getUserProfile();
    this.vessel = await getVessel();
  },
  mounted() {
    this.initStripe();
    this.checkCartProducts();
    this.getEstimates(this.cartProducts, this.formData);
  },
};
</script>

<style lang="scss" scoped></style>
